// src/hooks/useMicrosite.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMicrositePage,
  createMicrositePage,
  updateMicrositePage,
  deleteMicrositePage,
  getMicrositeUploadUrl,
  getMicrositeAnalytics,
  exportMicrositeSignups,
  getMicrositeSignupsEmailList,
  createMicrositeTrackableUrl,
  deleteMicrositeTrackableUrl,
} from '@/api/microsite';
import type { MicrositeFormState } from '@/types/microsite';

/** Fetch the current user's microsite page. */
export function useMicrositePage() {
  return useQuery({
    queryKey: ['microsite'],
    queryFn: getMicrositePage,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (no page exists yet)
      if (error?.code === 'NOT_FOUND' || error?.status === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Create a new microsite page. */
export function useCreateMicrositePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MicrositeFormState) => createMicrositePage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite'] });
    },
  });
}

/** Update the existing microsite page. */
export function useUpdateMicrositePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<MicrositeFormState>) => updateMicrositePage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite'] });
    },
  });
}

/** Delete the microsite page. */
export function useDeleteMicrositePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMicrositePage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite'] });
    },
  });
}

/** Get a pre-signed upload URL. */
export function useGetMicrositeUploadUrl() {
  return useMutation({
    mutationFn: ({ filename, contentType }: { filename: string; contentType: string }) =>
      getMicrositeUploadUrl(filename, contentType),
  });
}

/** Fetch analytics data. */
export function useMicrositeAnalytics(range: '7d' | '30d' = '30d') {
  return useQuery({
    queryKey: ['microsite-analytics', range],
    queryFn: () => getMicrositeAnalytics(range),
  });
}

/** Export signups as CSV. */
export function useExportMicrositeSignups() {
  return useMutation({
    mutationFn: async () => {
      const blob = await exportMicrositeSignups();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'microsite-signups.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

/** Download email list as a text file. */
export function useDownloadMicrositeEmailList() {
  return useMutation({
    mutationFn: async () => {
      const emailList = await getMicrositeSignupsEmailList();
      const blob = new Blob([emailList], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'microsite-signups-email-list.txt';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

/** Create a trackable URL for the microsite page. */
export function useCreateMicrositeTrackableUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createMicrositeTrackableUrl(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite'] });
      queryClient.invalidateQueries({ queryKey: ['microsite-analytics'] });
    },
  });
}

/** Delete a trackable URL from the microsite page. */
export function useDeleteMicrositeTrackableUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (trackingId: string) => deleteMicrositeTrackableUrl(trackingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite'] });
      queryClient.invalidateQueries({ queryKey: ['microsite-analytics'] });
    },
  });
}
